// Report/email previews are rendered into an iframe via srcDoc. The HTML is the real email, so it
// contains real dashboard links ("View Full Report", "Review & Send Report"). Activating one
// navigates the iframe itself -- the anchors carry no target -- which loads the entire dashboard
// nested inside the preview modal.
//
// Three layers, because CSS alone is not enough: `pointer-events: none` only suppresses pointer
// hit-testing, so a keyboard user could still Tab to an anchor and press Enter, and the iframe would
// navigate exactly as before.
//
//  1. `<base target="_blank">` retargets every activation away from the frame itself. Combined with
//     an iframe `sandbox` that withholds `allow-popups`, the browser blocks the navigation outright.
//     This is the layer that actually makes the preview inert, and it is modality-independent --
//     mouse, keyboard, assistive tech and programmatic .click() all take the same path.
//  2. `tabindex="-1"` takes the anchors out of the tab order so a keyboard user cannot land on a
//     dead link, and `aria-disabled` tells assistive tech the same thing.
//  3. The stylesheet removes the pointer affordance, so nothing appears clickable on hover.
//
// `href` is deliberately preserved: dropping it would stop the anchors matching the template's own
// `a`/`:link` styling, so the preview would no longer render like the delivered email. `<base>` is
// safe here for the same reason it is limited -- with no `href` attribute of its own it only sets the
// default target and does not change how relative URLs (or the templates' cid: images) resolve.
//
// Still not a security boundary: it neutralises our own template output for the person previewing it.
// The iframe's `sandbox` attribute is what provides isolation, and it must keep withholding
// `allow-popups` for layer 1 to hold.
const INERT_LINKS_HEAD =
  '<base target="_blank"><style>a{pointer-events:none!important;cursor:default!important;}</style>';

export const disableLinksInPreview = (html: string | undefined | null) => {
  if (!html) return html ?? undefined;

  // `<a\s` rather than `<a` so tags that merely start with "a" (<article>, <abbr>) cannot match. An
  // attribute-less `<a>` is skipped, which is fine -- with no href there is nothing to activate.
  const inertAnchors = html.replace(
    /<a\s/gi,
    '<a tabindex="-1" aria-disabled="true" ',
  );

  // Injected at the end of <head> so the rule wins over the template's own styles. A bare fragment
  // with no <head> gets a minimal one, which keeps <base> somewhere valid rather than loose in body.
  return /<\/head>/i.test(inertAnchors)
    ? inertAnchors.replace(/<\/head>/i, `${INERT_LINKS_HEAD}</head>`)
    : `<head>${INERT_LINKS_HEAD}</head>${inertAnchors}`;
};
